<template>
  <Container hideFooter title="Visitor sign-in">
    <template v-slot:body>
      <div class="flow-layout guest-flow">
        <section class="flow-intro">
          <div class="step-badge">
            <span>01</span><i></i><span>VISITOR</span>
          </div>
          <h1>Welcome in.</h1>
          <p class="flow-lede">
            Tell us a little about your visit and we’ll let your Bohm contact
            know you’ve arrived.
          </p>

          <div class="intro-points">
            <div class="intro-point">
              <span class="point-icon point-icon--orange"
                ><IconGlyph name="user" :size="19"
              /></span>
              <span
                ><strong>One quick form</strong
                ><small>Just the details we need.</small></span
              >
            </div>
            <div class="intro-point">
              <span class="point-icon point-icon--accent"
                ><IconGlyph name="shield" :size="19"
              /></span>
              <span
                ><strong>Your information is safe</strong
                ><small>Used only for your visit.</small></span
              >
            </div>
          </div>

          <button
            class="disclaimer-link"
            type="button"
            name="disclaimer"
            @click="dialog = true"
          >
            <IconGlyph name="info" :size="17" />
            <span>View visitor safety information</span>
          </button>
        </section>

        <section class="form-card" aria-label="Visitor sign-in form">
          <div class="form-card-heading">
            <div>
              <span class="section-overline">Let’s get started</span>
              <h2>Your details</h2>
            </div>
            <span class="required-note"><span>*</span> Required</span>
          </div>

          <form @submit.prevent="handleSignIn">
            <div class="field-group">
              <label class="field-label" for="visitor-name"
                >Your name <span>*</span></label
              >
              <input
                id="visitor-name"
                v-model="name"
                class="kiosk-input"
                autocomplete="off"
                placeholder="First and last name"
                type="text"
              />
            </div>

            <div class="field-group field-group--reason">
              <span class="field-label">I’m here for <span>*</span></span>
              <div class="choice-grid">
                <button
                  v-for="item in reasonOptions"
                  :key="item.value"
                  class="choice-card"
                  :class="{ 'choice-card--selected': reason === item.value }"
                  type="button"
                  @click="reason = item.value"
                >
                  <span class="choice-check"
                    ><IconGlyph name="check" :size="13"
                  /></span>
                  <span class="choice-copy"
                    ><strong>{{ item.label }}</strong
                    ><small>{{ item.help }}</small></span
                  >
                </button>
              </div>
            </div>

            <div v-if="reason === reasons[0]" class="conditional-fields">
              <div class="field-group">
                <label class="field-label" for="visitor-company"
                  >Company or affiliation
                  <span class="optional">Optional</span></label
                >
                <input
                  id="visitor-company"
                  v-model="company"
                  class="kiosk-input"
                  autocomplete="off"
                  placeholder="Company name"
                  type="text"
                />
              </div>

              <div class="field-group">
                <label class="field-label" for="visitor-host"
                  >Who are you here to see?
                  <span class="optional">Optional</span></label
                >
                <input
                  id="visitor-host"
                  v-model="host"
                  class="kiosk-input"
                  autocomplete="off"
                  placeholder="Host name"
                  type="text"
                />
              </div>
            </div>

            <div class="form-card-bottom">
              <span class="form-hint"
                ><span class="hint-dot"></span> You’ll receive a visitor badge
                at the desk.</span
              >
              <button
                class="primary-cta"
                name="signin"
                type="submit"
                :disabled="loading"
              >
                <span>{{ loading ? "Checking you in…" : "Continue" }}</span>
                <IconGlyph v-if="!loading" name="arrow-right" :size="20" />
              </button>
            </div>
          </form>
        </section>
      </div>

      <v-dialog
        v-model="dialog"
        max-width="690px"
        content-class="kiosk-dialog-wrap"
      >
        <div class="kiosk-dialog">
          <div class="dialog-heading">
            <span class="dialog-icon"
              ><IconGlyph name="shield" :size="23"
            /></span>
            <div>
              <span class="section-overline">Before you arrive</span>
              <h2>Visitor safety</h2>
            </div>
            <button
              type="button"
              class="dialog-close"
              aria-label="Close"
              @click="dialog = false"
            >
              ×
            </button>
          </div>
          <div class="dialog-copy">
            <p>{{ disclaimer }}</p>
            <p>{{ contractorDisclaimer }}</p>
          </div>
          <button type="button" class="dialog-done" @click="dialog = false">
            Got it
          </button>
        </div>
      </v-dialog>

      <v-dialog
        v-model="successDialog"
        max-width="500px"
        persistent
        content-class="kiosk-dialog-wrap"
      >
        <div class="kiosk-dialog kiosk-dialog--success">
          <span class="success-mark"><IconGlyph name="check" :size="32"/></span>
          <span class="section-overline">You’re all set</span>
          <h2>Thanks, {{ firstName }}!</h2>
          <p>
            Your host has been notified. Please take a seat and we’ll be with
            you shortly.
          </p>
          <div class="success-progress"><span></span></div>
          <small>Returning to the welcome screen…</small>
        </div>
      </v-dialog>
    </template>
  </Container>
</template>

<script>
import { mapActions } from "vuex";
import Container from "@/components/Container";
import IconGlyph from "@/components/IconGlyph";

export default {
  name: "Guest",
  components: { Container, IconGlyph },
  data() {
    return {
      name: "",
      company: "",
      host: "",
      reason: "",
      dialog: false,
      successDialog: false,
      loading: false,
      interval: null,
      reasons: ["Meeting", "Application/Interview", "Other"],
      reasonOptions: [
        { value: "Meeting", label: "A meeting", help: "I’m seeing someone" },
        {
          value: "Application/Interview",
          label: "An interview",
          help: "I’m applying"
        },
        {
          value: "Other",
          label: "Something else",
          help: "I’ll explain in person"
        }
      ],
      disclaimer:
        "Please see the emergency exit map and go to the nearest exit in the event of a fire. Muster points are in the front and back of the building. In the event of a tornado, shelter in place in the front offices by the cubicles.",
      contractorDisclaimer:
        "If you are a contractor, please make sure you are familiar with our safety guidelines and follow all OSHA standards while you are on the premises."
    };
  },
  computed: {
    firstName() {
      return (this.name || "guest").trim().split(" ")[0];
    }
  },
  beforeDestroy() {
    clearTimeout(this.interval);
  },
  methods: {
    ...mapActions(["signIn", "printGuestTag"]),
    async handleSignIn() {
      if (!this.name.trim()) {
        this.$store.dispatch("setSnackbar", {
          text: "Please enter your name.",
          color: "error"
        });
        return;
      }
      if (!this.reason) {
        this.$store.dispatch("setSnackbar", {
          text: "Please choose a reason for your visit.",
          color: "error"
        });
        return;
      }

      this.loading = true;
      try {
        const visitor = {
          name: this.name.trim(),
          reason: this.reason.trim(),
          company: this.company.trim(),
          packages: "",
          employee: this.host.trim() || null,
          type: "guest",
          signedIn: true
        };
        await this.printGuestTag({
          name: visitor.name,
          company: visitor.company
        });
        await this.signIn(visitor);
        this.successDialog = true;
        this.interval = setTimeout(() => {
          this.successDialog = false;
          this.$router.push("/");
        }, 5000);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style>
.flow-layout {
  display: grid;
  grid-template-columns: minmax(280px, 0.75fr) minmax(520px, 1.25fr);
  align-items: center;
  gap: clamp(42px, 8vw, 145px);
  min-height: calc(100vh - 189px);
}

.flow-intro {
  max-width: 410px;
  padding: 18px 0 30px clamp(10px, 3vw, 48px);
}

.step-badge {
  display: flex;
  align-items: center;
  gap: 9px;
  color: var(--orange);
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.16em;
}

.step-badge i {
  width: 34px;
  height: 1px;
  background: var(--orange);
  opacity: 0.55;
}

.flow-intro h1 {
  margin: 22px 0 15px;
  color: var(--ink);
  font-family: Eurosoft, -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  font-size: clamp(53px, 5vw, 76px);
  font-weight: 500;
  letter-spacing: -0.055em;
  line-height: 0.95;
}

.flow-lede {
  max-width: 360px;
  margin: 0;
  color: var(--muted);
  font-size: 15px;
  line-height: 1.65;
}

.intro-points {
  display: grid;
  gap: 14px;
  margin: 34px 0 34px;
}

.intro-point {
  display: flex;
  align-items: center;
  gap: 11px;
}

.point-icon {
  display: grid;
  width: 39px;
  height: 39px;
  border-radius: 12px;
  place-items: center;
}

.point-icon--orange {
  background: #fae1d9;
  color: var(--orange);
}

.point-icon--accent {
  background: var(--accent-soft);
  color: var(--accent);
}

.intro-point > span:last-child {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.intro-point strong {
  color: var(--ink);
  font-size: 12px;
}

.intro-point small {
  color: var(--muted);
  font-size: 11px;
}

.disclaimer-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--muted);
  font: inherit;
  font-size: 11px;
  font-weight: 800;
  cursor: pointer;
}

.disclaimer-link:hover,
.disclaimer-link:focus-visible {
  color: var(--orange);
  outline: none;
}

.form-card {
  width: 100%;
  max-width: 680px;
  justify-self: end;
  padding: clamp(28px, 3.5vw, 44px);
  box-sizing: border-box;
  border: 1px solid rgba(30, 41, 42, 0.08);
  border-radius: 25px;
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 24px 60px rgba(30, 41, 42, 0.08);
}

.form-card-heading,
.form-card-bottom {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
}

.form-card-heading {
  align-items: flex-start;
  margin-bottom: 30px;
}

.section-overline {
  display: block;
  font-size: 10px;
}

.form-card h2 {
  margin: 8px 0 0;
  color: var(--ink);
  font-size: 25px;
  font-weight: 850;
  letter-spacing: -0.04em;
}

.required-note {
  color: var(--muted);
  font-size: 10px;
  font-weight: 700;
}

.required-note span,
.field-label > span:not(.optional) {
  color: var(--orange);
}

.field-group {
  margin-bottom: 20px;
}

.field-label {
  display: block;
  margin-bottom: 8px;
  color: var(--ink);
  font-size: 12px;
  font-weight: 850;
}

.field-label .optional {
  margin-left: 6px;
  color: #99a19c;
  font-size: 10px;
  font-weight: 700;
}

.kiosk-input,
.kiosk-select {
  width: 100%;
  min-height: 52px;
  padding: 0 16px;
  box-sizing: border-box;
  border: 1px solid #dfe3dd;
  border-radius: 12px;
  outline: 0;
  background: #fbfbf9;
  color: var(--ink);
  font: inherit;
  font-size: 14px;
  transition: border 160ms ease, box-shadow 160ms ease, background 160ms ease;
}

.kiosk-input::placeholder {
  color: #aab1ad;
}

.kiosk-input:focus,
.kiosk-select:focus {
  border-color: var(--accent);
  background: white;
  box-shadow: 0 0 0 4px rgba(77, 86, 84, 0.1);
}

.choice-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 9px;
}

.choice-card {
  position: relative;
  display: flex;
  min-height: 70px;
  padding: 13px 10px 12px 13px;
  border: 1px solid #dfe3dd;
  border-radius: 12px;
  background: #fbfbf9;
  color: var(--ink);
  text-align: left;
  cursor: pointer;
  transition: 160ms ease;
}

.choice-card:hover {
  border-color: #d7b58f;
  background: #f8f4ec;
}

.choice-card--selected {
  border-color: var(--accent);
  background: #f5eee6;
  box-shadow: inset 0 0 0 1px var(--accent);
}

.choice-check {
  display: grid;
  width: 17px;
  height: 17px;
  margin-right: 8px;
  border: 1px solid #c9d0cc;
  border-radius: 50%;
  color: transparent;
  place-items: center;
}

.choice-card--selected .choice-check {
  border-color: var(--accent);
  background: var(--accent);
  color: white;
}

.choice-copy {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.choice-copy strong {
  font-size: 11px;
}

.choice-copy small {
  color: var(--muted);
  font-size: 9px;
  line-height: 1.2;
}

.conditional-fields {
  display: grid;
  grid-template-columns: 0.82fr 1.18fr;
  gap: 13px;
}

.conditional-fields .field-group {
  min-width: 0;
}

.form-card-bottom {
  align-items: center;
  gap: 16px;
  margin-top: 26px;
  padding-top: 22px;
  border-top: 1px solid #ecece7;
}

.form-hint {
  display: flex;
  align-items: center;
  gap: 7px;
  color: var(--muted);
  font-size: 10px;
  line-height: 1.4;
}

.hint-dot {
  width: 6px;
  height: 6px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: var(--yellow);
}

.primary-cta,
.dialog-done {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  min-width: 142px;
  min-height: 51px;
  padding: 0 19px;
  border: 0;
  border-radius: 12px;
  background: var(--orange);
  color: white;
  font: inherit;
  font-size: 12px;
  font-weight: 850;
  cursor: pointer;
  box-shadow: 0 8px 15px rgba(233, 86, 53, 0.18);
  transition: 160ms ease;
}

.primary-cta:hover,
.primary-cta:focus-visible,
.dialog-done:hover,
.dialog-done:focus-visible {
  background: var(--orange-dark);
  box-shadow: 0 11px 19px rgba(233, 86, 53, 0.24);
  outline: none;
}

.primary-cta:disabled {
  cursor: wait;
  opacity: 0.65;
}

.kiosk-dialog {
  position: relative;
  padding: 31px;
  border-radius: 21px;
  background: var(--white);
  box-shadow: 0 25px 70px rgba(30, 41, 42, 0.23);
}

.dialog-heading {
  display: flex;
  align-items: center;
  gap: 13px;
  padding-bottom: 22px;
  border-bottom: 1px solid var(--line);
}

.dialog-icon {
  display: grid;
  width: 47px;
  height: 47px;
  border-radius: 14px;
  background: var(--accent-soft);
  color: var(--accent);
  place-items: center;
}

.kiosk-dialog h2 {
  margin: 6px 0 0;
  color: var(--ink);
  font-size: 24px;
  font-weight: 850;
  letter-spacing: -0.04em;
}

.dialog-close {
  position: absolute;
  top: 22px;
  right: 24px;
  width: 33px;
  height: 33px;
  border: 0;
  border-radius: 50%;
  background: #f2f4f0;
  color: var(--muted);
  font-size: 25px;
  line-height: 1;
  cursor: pointer;
}

.dialog-copy {
  padding: 18px 4px 12px;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.6;
}

.dialog-copy p + p {
  margin-top: 15px;
}

.dialog-done {
  min-width: 100px;
  min-height: 43px;
  margin-top: 8px;
}

.kiosk-dialog--success {
  padding: 43px 40px 38px;
  text-align: center;
}

.success-mark {
  display: grid;
  width: 69px;
  height: 69px;
  margin: 0 auto 23px;
  border-radius: 50%;
  background: var(--accent-soft);
  color: var(--accent);
  place-items: center;
}

.kiosk-dialog--success h2 {
  margin-top: 11px;
  font-size: 29px;
}

.kiosk-dialog--success p {
  max-width: 340px;
  margin: 14px auto 24px;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.6;
}

.success-progress {
  width: 100%;
  height: 4px;
  overflow: hidden;
  border-radius: 99px;
  background: #ebeae4;
}

.success-progress span {
  display: block;
  width: 100%;
  height: 100%;
  transform-origin: left;
  background: var(--accent);
  animation: success-progress 5s linear forwards;
}

.kiosk-dialog--success small {
  display: block;
  margin-top: 12px;
  color: #9ba6a1;
  font-size: 10px;
}

@keyframes success-progress {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}

@media (max-width: 1000px) {
  .flow-layout {
    grid-template-columns: 1fr;
    gap: 24px;
    min-height: 0;
  }

  .flow-intro {
    max-width: 680px;
    padding-left: 0;
  }

  .flow-intro h1 {
    margin-top: 14px;
    font-size: 56px;
  }

  .intro-points {
    display: flex;
    gap: 24px;
    margin: 22px 0;
  }

  .form-card {
    max-width: none;
    justify-self: stretch;
  }
}

@media (max-width: 620px) {
  .choice-grid,
  .conditional-fields {
    grid-template-columns: 1fr;
  }

  .form-card-bottom {
    align-items: stretch;
    flex-direction: column;
  }

  .primary-cta {
    width: 100%;
  }
}
</style>
